'use client';

/**
 * Browser-side error reporter.
 *
 * Captures three classes of errors and POSTs them to /api/errors/ingest:
 *   1. Uncaught JS exceptions (`window.onerror`)
 *   2. Unhandled promise rejections (`window.onunhandledrejection`)
 *   3. (Optional, lightweight) console.error — disabled by default
 *
 * Mount once in the root layout. Silent on success and on failure — never
 * surface a "we couldn't report your error" message to the user.
 *
 * Filters noise:
 *   - ResizeObserver loop errors (browser bug, harmless)
 *   - Script error. (cross-origin, no detail available anyway)
 *   - Third-party script errors (Stripe, GTM, etc.) — we can't fix those
 *   - Network errors during the report itself
 */

import { useEffect } from 'react';
import { installBreadcrumbCapture, getBreadcrumbs } from '@/lib/breadcrumbs.client';
import { detectBotUserAgent, isClientNoise } from '@/lib/errorEnrichment';
import { logAsset404, logApiFailure } from '@/lib/notFoundLogger.client';

// In-page de-dup: same fingerprint within 30s = ignore. Prevents a busy
// loop from sending thousands of identical reports.
const recentlySent = new Map<string, number>();
const DEDUP_WINDOW_MS = 30_000;

function shouldReport(message: string, source: string | null): boolean {
  if (!message) return false;
  if (isClientNoise(message)) return false;
  // Errors from third-party scripts often have no source filename or one
  // that's clearly not ours — skip those.
  if (source && !source.includes(window.location.host) && !source.startsWith('/')) {
    return false;
  }
  return true;
}

function fingerprint(message: string, stack: string): string {
  const firstLine = message.split('\n')[0].slice(0, 200);
  const firstFrame = stack.split('\n').find((l) => l.trim()) || '';
  return `${firstLine}::${firstFrame}`.slice(0, 256);
}

function recentlyReported(key: string): boolean {
  const now = Date.now();
  // Clean old entries.
  for (const [k, ts] of recentlySent) {
    if (now - ts > DEDUP_WINDOW_MS) recentlySent.delete(k);
  }
  if (recentlySent.has(key)) return true;
  recentlySent.set(key, now);
  return false;
}

async function report(payload: Record<string, unknown>) {
  try {
    // Use keepalive so the request survives a navigation/close.
    await fetch('/api/errors/ingest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Never throw — the user's experience must not get worse because
    // error reporting failed.
  }
}

export default function ErrorReporter() {
  useEffect(() => {
    // Start collecting breadcrumbs ASAP so any error fired later has the
    // user's recent activity attached.
    installBreadcrumbCapture();

    const onError = (ev: ErrorEvent) => {
      const message = ev.message || (ev.error && ev.error.message) || 'Unknown error';
      const stack = (ev.error && ev.error.stack) || '';
      if (!shouldReport(message, ev.filename)) return;
      const key = fingerprint(message, stack);
      if (recentlyReported(key)) return;

      const bot = detectBotUserAgent(navigator.userAgent);
      report({
        source: 'client',
        message,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        release: (window as any).__APP_RELEASE__ || undefined,
        context: {
          filename: ev.filename,
          line: ev.lineno,
          col: ev.colno,
          breadcrumbs: getBreadcrumbs(),
          bot: bot.isBot,
          botLabel: bot.label,
        },
      });
    };

    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const message =
        (reason instanceof Error ? reason.message : String(reason || '')) ||
        'Unhandled promise rejection';
      const stack = reason instanceof Error ? reason.stack || '' : '';
      if (!shouldReport(message, null)) return;
      const key = fingerprint(message, stack);
      if (recentlyReported(key)) return;

      const bot = detectBotUserAgent(navigator.userAgent);
      report({
        source: 'client',
        message: `[unhandledrejection] ${message}`,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        release: (window as any).__APP_RELEASE__ || undefined,
        context: {
          breadcrumbs: getBreadcrumbs(),
          bot: bot.isBot,
          botLabel: bot.label,
        },
      });
    };

    // Capture-phase listener for resource load failures (image/script/link).
    // The bubbling-phase 'error' listener does NOT see these — that's why
    // the Mobile "Shop by Popular Style" missing-variant 404s went unlogged
    // until we noticed them visually. Capture phase catches them globally.
    //
    // CRITICAL: use `currentSrc` (the URL the browser actually tried to
    // load — picked from srcset on responsive images) NOT `src` (the
    // attribute value / fallback). Reporting `src` made earlier triage
    // misleading — we'd see "foo.png" in the bug board while the real
    // failure was at "foo-w640.png" from the srcset.
    const onResourceError = (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName?.toLowerCase();
      if (tag !== 'img' && tag !== 'script' && tag !== 'link') return;
      const url =
        (target as HTMLImageElement).currentSrc ||
        (target as HTMLImageElement).src ||
        (target as HTMLLinkElement).href ||
        '';
      if (!url) return;
      logAsset404(url, tag);
    };

    // --- 4. Failed API calls -------------------------------------------------
    // The three listeners above cannot see these. A fetch() that comes back 404
    // or 500 RESOLVES — it throws nothing, rejects nothing, and loads no
    // resource — so a stale endpoint just makes a feature quietly do nothing.
    // That is how ~20 dead endpoints (search autocomplete, the installer
    // dashboard, account payment methods) ran unnoticed. We wrap fetch, observe
    // the status, and always hand back the untouched response.
    const originalFetch = window.fetch;
    const wrappedFetch: typeof window.fetch = async (input, init) => {
      const response = await originalFetch(input as any, init);
      try {
        const rawUrl =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : (input as Request)?.url || '';
        // Only our own API. Third-party failures (analytics, Stripe) are not
        // ours to fix, and reporting the reporter would loop.
        const sameOrigin = rawUrl.startsWith('/') ||
          (() => { try { return new URL(rawUrl).origin === window.location.origin; } catch { return false; } })();
        // A 403 without a JSON body came from Cloudflare or the middleware bot
        // wall, not our API (which always answers JSON): a blocked client, not
        // a broken endpoint.
        const edgeBlock =
          response.status === 403 &&
          !(response.headers.get('content-type') || '').includes('application/json');
        if (sameOrigin && /\/api\//.test(rawUrl) && !response.ok && !edgeBlock) {
          const method = (init?.method || (input as Request)?.method || 'GET').toUpperCase();
          void logApiFailure({
            url: rawUrl,
            status: response.status,
            method,
            pageUrl: window.location.pathname + window.location.search,
          });
        }
      } catch {
        /* a logger must never break the request it is observing */
      }
      return response;
    };
    window.fetch = wrappedFetch;

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onResourceError, { capture: true });
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onResourceError, { capture: true } as any);
      // Only restore if nothing else wrapped fetch after us.
      if (window.fetch === wrappedFetch) window.fetch = originalFetch;
    };
  }, []);

  return null;
}
